import React, { useEffect, useState } from 'react';
import { WizardStepWrapper } from '../step';
import {
  H2,
  IndicatesRequired,
  StackedColumn,
  StackedColumnSpacing,
  TextAreaField,
  isPhoneValid,
} from '../../../../core';
import {
  ReminderOption,
  ReviewAndSubmitDetails,
  SchedulingDetails,
} from '../../../../api/models';
import { ReminderOptions } from './reminderOptions';
import { isMinor } from '../../../../utils';
import { TextAreaResize } from '../../../../core/components/inputs/models';
import { DateUtility } from '@mchs/web-core';

export interface ReviewAndSubmitStepPresenterProps {
  schedulingDetails: SchedulingDetails;
  onMobilePhoneChanged: (value: string) => void;
  onSubmit: (reviewAndSubmitDetails: ReviewAndSubmitDetails) => void;
}

const determineDefaultOption = (dateOfbirth?: string): ReminderOption => {
  return dateOfbirth &&
    isMinor(DateUtility.getDateFromCattailsDateString(dateOfbirth))
    ? ReminderOption.Letter
    : ReminderOption.Text;
};

const ReviewAndSubmitStepPresenter: React.FC<
  ReviewAndSubmitStepPresenterProps
> = (props: ReviewAndSubmitStepPresenterProps) => {
  const [additionalDetails, setAdditionalDetails] = useState<string>('');
  const [patientAgrees, setPatientAgrees] = useState<boolean>(
    props.schedulingDetails.reviewAndSubmitDetails?.patientAgrees ?? false
  );
  const [validate, setValidate] = useState<boolean>(false);

  useEffect(() => {
    setAdditionalDetails(
      props.schedulingDetails.reviewAndSubmitDetails?.additionalDetails ?? ''
    );
    setPatientAgrees(
      props.schedulingDetails.reviewAndSubmitDetails?.patientAgrees ?? false
    );
  }, [
    props.schedulingDetails.patientDetails,
    props.schedulingDetails.reviewAndSubmitDetails,
  ]);

  const dateOfBirth: Date | undefined =
    DateUtility.getDateNullableFromCattailsDateString(
      props.schedulingDetails.patientDetails?.demographics?.dateOfBirth ?? null
    );
  const patientIsMinor = dateOfBirth && isMinor(dateOfBirth);

  const onContinue = () => {
    if (isValid()) {
      props.onSubmit({
        additionalDetails: additionalDetails,
        reminderOption: determineDefaultOption(
          props.schedulingDetails.patientDetails?.demographics?.dateOfBirth
        ),
      });
    } else {
      setValidate(true);
    }
  };

  // return false to cancel onContinue
  const isValid = (): boolean => {
    return (
      additionalDetails.length > 0 &&
      (patientIsMinor || patientAgrees) &&
      isPhoneValid(
        props.schedulingDetails.contactInfoDetails?.patientContactDetails
          .mobileNumber ?? '',
        false
      ) &&
      true
    );
  };

  const onChangePatientAgrees = (patientAgrees: boolean) => {
    setPatientAgrees(patientAgrees);
  };

  return (
    <WizardStepWrapper
      continueText="Schedule appointment"
      onContinue={onContinue}
      collapsableDetail={false}
      stickyWhenNarrow={false}
    >
      <StackedColumn spacing={StackedColumnSpacing.Wider}>
        <StackedColumn spacing={StackedColumnSpacing.Wide}>
          <StackedColumn>
            <H2 text="Please fill out additional information about the appointment." />
            <IndicatesRequired />
          </StackedColumn>
          <TextAreaField
            value={additionalDetails}
            label="Reason for the appointment"
            optionalText="Briefly describe the reason for this appointment, whether it's for managing a condition, a routine checkup, or a follow-up on test results or medication refills."
            required={props.schedulingDetails.appointmentInfo?.commentRequired}
            validate={validate}
            resize={TextAreaResize.Vertical}
            onChange={(value) => setAdditionalDetails(value)}
          />
        </StackedColumn>
        {patientIsMinor ? undefined : (
          <>
            <ReminderOptions
              patientContactDetails={
                props.schedulingDetails.contactInfoDetails
                  ?.patientContactDetails
              }
              patientAgrees={patientAgrees}
              validate={validate}
              onChangePatientAgrees={onChangePatientAgrees}
              onMobilePhoneChanged={props.onMobilePhoneChanged}
            />
          </>
        )}
      </StackedColumn>
    </WizardStepWrapper>
  );
};

export default ReviewAndSubmitStepPresenter;
